import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { buildApiUrl } from '../config/api'

export interface RfpPdfData {
  id: string
  title: string
  description: string
  requirements: string[]
  status: string
  category: string
  workType: 'REMOTE' | 'ONSITE' | 'HYBRID'
  country?: string
  budgetMin: number
  budgetMax: number
  budgetCurrency: string
  deadline: string
  createdAt: string
  user: {
    name: string
    company: string
  }
}

export interface ResponsePdfData {
  id: string
  message: string
  proposal: string
  budget: number
  timeline: string
  status: string
  createdAt: string
  updatedAt: string
  responder: {
    name: string
    company: string
    email: string
    phone?: string
    website?: string
    linkedinUrl?: string
  }
  rfp: {
    id: string
    title: string
    description: string
    requirements: string[]
    category: string
    budgetMin: number
    budgetMax: number
    budgetCurrency: string
    deadline: string
    createdAt: string
    client: {
      name: string
      company: string
    }
  }
}

export const generateRfpPdf = async (rfpData: RfpPdfData): Promise<void> => {
  // Create a temporary div element to render the RFP content
  const tempDiv = document.createElement('div')
  tempDiv.style.position = 'absolute'
  tempDiv.style.left = '-9999px'
  tempDiv.style.top = '0'
  tempDiv.style.width = '800px'
  tempDiv.style.padding = '40px'
  tempDiv.style.backgroundColor = 'white'
  tempDiv.style.fontFamily = 'Arial, sans-serif'
  tempDiv.style.fontSize = '12px'
  tempDiv.style.lineHeight = '1.6'
  tempDiv.style.color = '#333'

  // Create the HTML content for the PDF
  tempDiv.innerHTML = `
    <div style="text-align: center; margin-bottom: 30px;">
      <h1 style="color: #2563eb; font-size: 28px; margin-bottom: 10px;">Request for Proposal</h1>
      <h2 style="font-size: 20px; margin-bottom: 5px;">${rfpData.title}</h2>
      <p style="color: #666; font-size: 14px;">RFP ID: ${rfpData.id}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Company Information</h3>
      <p><strong>Company:</strong> ${rfpData.user.company}</p>
      <p><strong>Contact:</strong> ${rfpData.user.name}</p>
      <p><strong>Date Created:</strong> ${new Date(rfpData.createdAt).toLocaleDateString()}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Project Details</h3>
      <p><strong>Category:</strong> ${rfpData.category}</p>
      <p><strong>Work Type:</strong> ${rfpData.workType === 'REMOTE' ? 'Remote' : rfpData.workType === 'ONSITE' ? 'On-site' : 'Hybrid'}</p>
      ${rfpData.country ? `<p><strong>Country:</strong> ${rfpData.country}</p>` : ''}
      <p><strong>Status:</strong> ${rfpData.status}</p>
      <p><strong>Budget Range:</strong> ${rfpData.budgetCurrency} ${rfpData.budgetMin.toLocaleString()} - ${rfpData.budgetMax.toLocaleString()}</p>
      <p><strong>Deadline:</strong> ${new Date(rfpData.deadline).toLocaleDateString()}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Project Description</h3>
      <p style="text-align: justify;">${rfpData.description}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Requirements & Deliverables</h3>
      <ul style="margin-left: 20px;">
        ${rfpData.requirements.map(req => `<li style="margin-bottom: 8px;">${req}</li>`).join('')}
      </ul>
    </div>

    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; text-align: center; color: #666; font-size: 10px;">
      <p>Generated by SourcedIn RFP Management Platform</p>
      <p>Generated on: ${new Date().toLocaleString()}</p>
    </div>
  `

  // Add the temporary div to the document
  document.body.appendChild(tempDiv)

  try {
    // Convert the HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    })

    // Create PDF
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    
    const imgWidth = 210 // A4 width in mm
    const pageHeight = 295 // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width
    let heightLeft = imgHeight

    let position = 0

    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    // Add additional pages if content is longer than one page
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    // Save the PDF
    const fileName = `RFP_${rfpData.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
    pdf.save(fileName)

  } catch (error) {
    console.error('Error generating PDF:', error)
    throw new Error('Failed to generate PDF')
  } finally {
    // Clean up the temporary div
    document.body.removeChild(tempDiv)
  }
}

// Alternative method using jsPDF directly (without HTML rendering)
export const generateRfpPdfSimple = (rfpData: RfpPdfData): void => {
  const pdf = new jsPDF('p', 'mm', 'a4')
  
  // Set font
  pdf.setFont('helvetica')
  
  // Title
  pdf.setFontSize(20)
  pdf.setTextColor(37, 99, 235) // Blue color
  pdf.text('Request for Proposal', 105, 20, { align: 'center' })
  
  pdf.setFontSize(16)
  pdf.setTextColor(0, 0, 0)
  pdf.text(rfpData.title, 105, 35, { align: 'center' })
  
  // Company Information
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Company Information', 20, 55)
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  pdf.text(`Company: ${rfpData.user.company}`, 20, 65)
  pdf.text(`Contact: ${rfpData.user.name}`, 20, 72)
  pdf.text(`Date Created: ${new Date(rfpData.createdAt).toLocaleDateString()}`, 20, 79)
  
  // Project Details
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Project Details', 20, 95)
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  pdf.text(`Category: ${rfpData.category}`, 20, 105)
  pdf.text(`Work Type: ${rfpData.workType === 'REMOTE' ? 'Remote' : rfpData.workType === 'ONSITE' ? 'On-site' : 'Hybrid'}`, 20, 112)
  if (rfpData.country) {
    pdf.text(`Country: ${rfpData.country}`, 20, 119)
    pdf.text(`Status: ${rfpData.status}`, 20, 126)
    pdf.text(`Budget Range: ${rfpData.budgetCurrency} ${rfpData.budgetMin.toLocaleString()} - ${rfpData.budgetMax.toLocaleString()}`, 20, 133)
    pdf.text(`Deadline: ${new Date(rfpData.deadline).toLocaleDateString()}`, 20, 140)
  } else {
    pdf.text(`Status: ${rfpData.status}`, 20, 119)
    pdf.text(`Budget Range: ${rfpData.budgetCurrency} ${rfpData.budgetMin.toLocaleString()} - ${rfpData.budgetMax.toLocaleString()}`, 20, 126)
    pdf.text(`Deadline: ${new Date(rfpData.deadline).toLocaleDateString()}`, 20, 133)
  }
  
  // Description
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  const descriptionY = rfpData.country ? 156 : 149
  pdf.text('Project Description', 20, descriptionY)
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  const descriptionLines = pdf.splitTextToSize(rfpData.description, 170)
  const descriptionTextY = rfpData.country ? 166 : 159
  pdf.text(descriptionLines, 20, descriptionTextY)
  
  // Requirements
  let yPosition = descriptionTextY + (descriptionLines.length * 5) + 10
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Requirements & Deliverables', 20, yPosition)
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  yPosition += 10
  
  rfpData.requirements.forEach((req) => {
    const reqLines = pdf.splitTextToSize(`• ${req}`, 160)
    pdf.text(reqLines, 25, yPosition)
    yPosition += reqLines.length * 5 + 2
  })
  
  // Footer
  pdf.setFontSize(8)
  pdf.setTextColor(128, 128, 128)
  pdf.text('Generated by SourcedIn RFP Management Platform', 105, 280, { align: 'center' })
  pdf.text(`Generated on: ${new Date().toLocaleString()}`, 105, 285, { align: 'center' })
  
  // Save the PDF
  const fileName = `RFP_${rfpData.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
  pdf.save(fileName)
}

export const generateResponsePdf = async (responseData: ResponsePdfData): Promise<void> => {
  // Validate input data
  if (!responseData || !responseData.rfp || !responseData.responder) {
    throw new Error('Invalid response data provided')
  }

  // Check if required libraries are available
  if (typeof html2canvas === 'undefined' || typeof jsPDF === 'undefined') {
    throw new Error('PDF generation libraries not loaded')
  }

  // Create a temporary div element to render the response content
  const tempDiv = document.createElement('div')
  tempDiv.style.position = 'absolute'
  tempDiv.style.left = '-9999px'
  tempDiv.style.top = '0'
  tempDiv.style.width = '800px'
  tempDiv.style.padding = '40px'
  tempDiv.style.backgroundColor = 'white'
  tempDiv.style.fontFamily = 'Arial, sans-serif'
  tempDiv.style.fontSize = '12px'
  tempDiv.style.lineHeight = '1.6'
  tempDiv.style.color = '#333'

  // Create the HTML content for the response PDF with safe data handling
  const safeString = (str: any) => String(str || '').replace(/[<>]/g, '')
  const safeDate = (dateStr: any) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }

  tempDiv.innerHTML = `
    <div style="text-align: center; margin-bottom: 30px;">
      <h1 style="color: #2563eb; font-size: 28px; margin-bottom: 10px;">RFP Response</h1>
      <h2 style="font-size: 20px; margin-bottom: 5px;">${safeString(responseData.rfp.title)}</h2>
      <p style="color: #666; font-size: 14px;">Response ID: ${safeString(responseData.id)}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Responder Information</h3>
      <p><strong>Company:</strong> ${safeString(responseData.responder.company)}</p>
      <p><strong>Contact:</strong> ${safeString(responseData.responder.name)}</p>
      <p><strong>Email:</strong> ${safeString(responseData.responder.email)}</p>
      ${responseData.responder.phone ? `<p><strong>Phone:</strong> ${safeString(responseData.responder.phone)}</p>` : ''}
      ${responseData.responder.website ? `<p><strong>Website:</strong> ${safeString(responseData.responder.website)}</p>` : ''}
      ${responseData.responder.linkedinUrl ? `<p><strong>LinkedIn:</strong> ${safeString(responseData.responder.linkedinUrl)}</p>` : ''}
      <p><strong>Response Date:</strong> ${safeDate(responseData.createdAt)}</p>
      <p><strong>Status:</strong> <span style="background: ${responseData.status === 'ACCEPTED' ? '#10b981' : responseData.status === 'REJECTED' ? '#ef4444' : '#f59e0b'}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 10px;">${safeString(responseData.status)}</span></p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Response Details</h3>
      <p><strong>Proposed Budget:</strong> ${safeString(responseData.rfp.budgetCurrency)} ${(responseData.budget || 0).toLocaleString()}</p>
      <p><strong>Timeline:</strong> ${safeString(responseData.timeline)}</p>
      <p><strong>RFP Budget Range:</strong> ${safeString(responseData.rfp.budgetCurrency)} ${(responseData.rfp.budgetMin || 0).toLocaleString()} - ${(responseData.rfp.budgetMax || 0).toLocaleString()}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Cover Message</h3>
      <p style="text-align: justify; white-space: pre-wrap;">${safeString(responseData.message)}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Detailed Proposal</h3>
      <p style="text-align: justify; white-space: pre-wrap;">${safeString(responseData.proposal)}</p>
    </div>

    <div style="margin-bottom: 30px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px; border-bottom: 2px solid #2563eb; padding-bottom: 5px;">Original RFP Requirements</h3>
      <ul style="margin-left: 20px;">
        ${(responseData.rfp.requirements || []).map(req => `<li style="margin-bottom: 8px;">${safeString(req)}</li>`).join('')}
      </ul>
    </div>

    <div style="margin-bottom: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
      <h3 style="color: #2563eb; font-size: 16px; margin-bottom: 10px;">RFP Summary</h3>
      <p><strong>Client:</strong> ${safeString(responseData.rfp.client?.name)} (${safeString(responseData.rfp.client?.company)})</p>
      <p><strong>Category:</strong> ${safeString(responseData.rfp.category)}</p>
      <p><strong>Deadline:</strong> ${safeDate(responseData.rfp.deadline)}</p>
    </div>

    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; text-align: center; color: #666; font-size: 10px;">
      <p>Generated by SourcedIn RFP Management Platform</p>
      <p>Generated on: ${new Date().toLocaleString()}</p>
    </div>
  `

  // Add the temporary div to the document
  document.body.appendChild(tempDiv)

  try {
    // Convert the HTML to canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    })

    // Create PDF
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    
    const imgWidth = 210 // A4 width in mm
    const pageHeight = 295 // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width
    let heightLeft = imgHeight

    let position = 0

    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    // Add additional pages if content is longer than one page
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    // Save the PDF
    const safeFileName = (str: string) => str.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 50)
    const fileName = `Response_${safeFileName(responseData.rfp.title)}_${safeFileName(responseData.responder.name)}_${new Date().toISOString().split('T')[0]}.pdf`
    pdf.save(fileName)

  } catch (error) {
    console.error('Error generating response PDF:', error)
    throw new Error('Failed to generate response PDF')
  } finally {
    // Clean up the temporary div
    document.body.removeChild(tempDiv)
  }
}

// Alternative method for response PDF using jsPDF directly
export const generateResponsePdfSimple = (responseData: ResponsePdfData): void => {
  // Validate input data
  if (!responseData || !responseData.rfp || !responseData.responder) {
    throw new Error('Invalid response data provided')
  }

  // Check if jsPDF is available
  if (typeof jsPDF === 'undefined') {
    throw new Error('jsPDF library not loaded')
  }

  const pdf = new jsPDF('p', 'mm', 'a4')
  
  // Set font
  pdf.setFont('helvetica')
  
  // Title
  pdf.setFontSize(20)
  pdf.setTextColor(37, 99, 235) // Blue color
  pdf.text('RFP Response', 105, 20, { align: 'center' })
  
  pdf.setFontSize(16)
  pdf.setTextColor(0, 0, 0)
  const safeText = (text: any, maxLength = 100) => String(text || '').substring(0, maxLength)
  pdf.text(safeText(responseData.rfp.title), 105, 35, { align: 'center' })
  
  // Responder Information
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Responder Information', 20, 55)
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  let yPos = 65
  pdf.text(`Company: ${safeText(responseData.responder.company)}`, 20, yPos)
  yPos += 7
  pdf.text(`Contact: ${safeText(responseData.responder.name)}`, 20, yPos)
  yPos += 7
  pdf.text(`Email: ${safeText(responseData.responder.email)}`, 20, yPos)
  yPos += 7
  try {
    pdf.text(`Response Date: ${new Date(responseData.createdAt).toLocaleDateString()}`, 20, yPos)
  } catch {
    pdf.text(`Response Date: N/A`, 20, yPos)
  }
  yPos += 7
  pdf.text(`Status: ${safeText(responseData.status)}`, 20, yPos)
  yPos += 15
  
  // Response Details
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Response Details', 20, yPos)
  yPos += 10
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  pdf.text(`Proposed Budget: ${safeText(responseData.rfp.budgetCurrency)} ${(responseData.budget || 0).toLocaleString()}`, 20, yPos)
  yPos += 7
  pdf.text(`Timeline: ${safeText(responseData.timeline)}`, 20, yPos)
  yPos += 15
  
  // Cover Message
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Cover Message', 20, yPos)
  yPos += 10
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  const messageLines = pdf.splitTextToSize(safeText(responseData.message, 500), 170)
  pdf.text(messageLines, 20, yPos)
  yPos += messageLines.length * 5 + 10
  
  // Proposal
  if (yPos > 250) {
    pdf.addPage()
    yPos = 20
  }
  
  pdf.setFontSize(12)
  pdf.setTextColor(37, 99, 235)
  pdf.text('Detailed Proposal', 20, yPos)
  yPos += 10
  
  pdf.setFontSize(10)
  pdf.setTextColor(0, 0, 0)
  const proposalLines = pdf.splitTextToSize(safeText(responseData.proposal, 1000), 170)
  pdf.text(proposalLines, 20, yPos)
  
  // Footer
  pdf.setFontSize(8)
  pdf.setTextColor(128, 128, 128)
  pdf.text('Generated by SourcedIn RFP Management Platform', 105, 280, { align: 'center' })
  pdf.text(`Generated on: ${new Date().toLocaleString()}`, 105, 285, { align: 'center' })
  
  // Save the PDF
  const safeFileName = (str: string) => str.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 50)
  const fileName = `Response_${safeFileName(responseData.rfp.title)}_${safeFileName(responseData.responder.name)}_${new Date().toISOString().split('T')[0]}.pdf`
  pdf.save(fileName)
}

// Fetch RFP data from backend for PDF generation
export const fetchRfpDataForPdf = async (rfpId: string): Promise<RfpPdfData> => {
  const token = localStorage.getItem('token')
  if (!token) {
    throw new Error('Authentication required')
  }

  const response = await fetch(buildApiUrl(`/api/pdf/${rfpId}`), {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch RFP data')
  }

  const result = await response.json()
  return result.data
}

// Fetch Response data from backend for PDF generation
export const fetchResponseDataForPdf = async (responseId: string): Promise<ResponsePdfData> => {
  const token = localStorage.getItem('token')
  if (!token) {
    throw new Error('Authentication required')
  }

  const response = await fetch(buildApiUrl(`/api/pdf/response/${responseId}`), {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Backend error:', errorText)
    throw new Error(`Failed to fetch response data: ${response.status} ${response.statusText}`)
  }

  const result = await response.json()
  
  if (!result.success || !result.data) {
    throw new Error('Invalid response data from server')
  }
  
  return result.data
}

// Emergency fallback PDF generation for when all else fails
export const generateEmergencyResponsePdf = (responseData: ResponsePdfData): void => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4')
    
    // Simple title
    pdf.setFontSize(20)
    pdf.text('RFP Response Export', 105, 20, { align: 'center' })
    
    // Basic info
    pdf.setFontSize(12)
    pdf.text(`RFP: ${String(responseData.rfp?.title || 'Unknown')}`, 20, 40)
    pdf.text(`Responder: ${String(responseData.responder?.name || 'Unknown')}`, 20, 50)
    pdf.text(`Company: ${String(responseData.responder?.company || 'Unknown')}`, 20, 60)
    pdf.text(`Date: ${new Date().toLocaleDateString()}`, 20, 70)
    
    // Simple content
    pdf.setFontSize(10)
    const message = String(responseData.message || 'No message provided')
    const messageLines = pdf.splitTextToSize(message, 170)
    pdf.text(messageLines, 20, 90)
    
    // Save
    pdf.save(`Emergency_Response_${new Date().toISOString().split('T')[0]}.pdf`)
  } catch (error) {
    console.error('Emergency PDF generation failed:', error)
    throw new Error('All PDF generation methods failed')
  }
} 